/**
 * Classifies raw messages arriving on the File Picker v8 MessagePort.
 *
 * The picker sends two envelope kinds: `{ type: "command", id, data: { command, ... } }`
 * which must be acknowledged and answered, and `{ type: "notification", data }`
 * which is informational. Microsoft's `INotificationData` is
 * `{ notification, code, isExpected, message, timestamp }`; `isExpected: false`
 * is its severity signal (render failures, auth trouble).
 */
export type PickerCommandId = string | number;

export type PickerMessage =
  | {
      kind: "command";
      id: PickerCommandId;
      command: Record<string, unknown> & { command: string };
    }
  | { kind: "notification"; name: string; isError: boolean; payload: string }
  | {
      kind: "malformedCommand";
      id: PickerCommandId | null;
      reason: "noId" | "noData" | "noCommand";
      /** Keys the payload did carry, to diagnose a protocol change from telemetry. */
      keys: string[];
    }
  | { kind: "unknown"; messageType: string };

const MAX_PAYLOAD_LENGTH = 500;
const ERROR_NAME_PATTERN = /error|fail|denied|unauthori[sz]ed/i;
/** Fields worth keeping from an error notification; never the whole object. */
const PAYLOAD_FIELDS = [
  "notification",
  "code",
  "isExpected",
  "message",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isCommandId = (value: unknown): value is PickerCommandId =>
  typeof value === "string" || typeof value === "number";

const looksLikeError = (name: string, data: Record<string, unknown>) =>
  data.isExpected === false || ERROR_NAME_PATTERN.test(name);

const serializeErrorPayload = (data: Record<string, unknown>): string => {
  const projection: Record<string, unknown> = {};
  for (const field of PAYLOAD_FIELDS) {
    if (field in data) projection[field] = data[field];
  }
  try {
    return JSON.stringify(projection).slice(0, MAX_PAYLOAD_LENGTH);
  } catch {
    return "[unserializable]";
  }
};

const classifyNotification = (inner: unknown): PickerMessage => {
  const data = isRecord(inner) ? inner : {};
  const name =
    typeof data.notification === "string" ? data.notification : "unknown";
  const isError = looksLikeError(name, data);

  return {
    kind: "notification",
    name,
    isError,
    payload: isError ? serializeErrorPayload(data) : "",
  };
};

const classifyCommand = (envelope: Record<string, unknown>): PickerMessage => {
  const id = isCommandId(envelope.id) ? envelope.id : null;
  if (id === null) {
    return { kind: "malformedCommand", id, reason: "noId", keys: [] };
  }

  const inner = envelope.data;
  if (!isRecord(inner)) {
    return { kind: "malformedCommand", id, reason: "noData", keys: [] };
  }

  if (inner.command == null) {
    return {
      kind: "malformedCommand",
      id,
      reason: "noCommand",
      keys: Object.keys(inner),
    };
  }

  return {
    kind: "command",
    id,
    command: { ...inner, command: String(inner.command) },
  };
};

export const classifyPickerMessage = (raw: unknown): PickerMessage => {
  if (!isRecord(raw)) return { kind: "unknown", messageType: typeof raw };

  switch (raw.type) {
    case "notification":
      return classifyNotification(raw.data);
    case "command":
      return classifyCommand(raw);
    default:
      return { kind: "unknown", messageType: String(raw.type) };
  }
};
